import { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useSavePayslip, useSaveBizPayslip } from "../../../hooks/usePayslip";
import { payslipApi, type PayslipDeduction, type PpaAdjustment, type CashAdvanceDeductionEntry } from "../../../lib/payslipApi";
import { payrollApi, bizPayrollApi } from "../../../lib/payrollApi";
import type { DynamicDeduction, PayrollConfig } from "../../../lib/payrollApi";
import { formatCurrency, formatPeriod } from "../../../utils/format";

interface Props {
  userId: string;
  period: string;
  businessId?: string;
  onClose: () => void;
  onSaved: () => void;
}

function applyRule(d: DynamicDeduction, basis: number): number {
  const raw = d.type === "PERCENTAGE" ? (basis * d.value) / 100 : d.value;
  return Math.round(raw * 100) / 100;
}

function buildDefaultsFromConfig(
  basicPay: number,
  config: PayrollConfig,
): { allowances: PayslipDeduction[]; deductions: PayslipDeduction[] } {
  return {
    allowances: config.allowances
      .filter((a) => a.enabled)
      .map((a) => ({ label: a.name, amount: a.amount })),
    deductions: config.govDeductions
      .filter((d) => d.enabled)
      .map((d) => ({ label: d.name, amount: applyRule(d, basicPay) })),
  };
}

export function ProcessPayslipModal({ userId, period, businessId, onClose, onSaved }: Props) {
  const { toasts, toast, dismissToast } = useToast();
  const saveBizPayslip = useSaveBizPayslip(businessId ?? "");
  const saveAdminPayslip = useSavePayslip();
  const savePayslip = businessId ? saveBizPayslip : saveAdminPayslip;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [profileName, setProfileName] = useState("");
  const [profilePosition, setProfilePosition] = useState("");

  const [basicPay, setBasicPay] = useState(0);
  const [allowances, setAllowances] = useState<PayslipDeduction[]>([]);
  const [incentives, setIncentives] = useState<PayslipDeduction[]>([]);
  const [deductions, setDeductions] = useState<PayslipDeduction[]>([]);
  const [loanDeductions, setLoanDeductions] = useState<PayslipDeduction[]>([]);
  const [ppaAdjustments, setPpaAdjustments] = useState<PpaAdjustment[]>([]);
  const [cashAdvanceDeductions, setCashAdvanceDeductions] = useState<CashAdvanceDeductionEntry[]>([]);
  const [workAdjustments, setWorkAdjustments] = useState<PayslipDeduction[]>([]);
  const [existingStatus, setExistingStatus] = useState<"DRAFT" | "FINALIZED" | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [payslipData, configData] = await Promise.all([
          businessId
            ? payslipApi.bizAdminGet(businessId, userId, period)
            : payslipApi.adminGet(userId, period),
          businessId
            ? bizPayrollApi.getConfig(businessId)
            : payrollApi.getConfig(),
        ]);
        if (cancelled) return;

        const config = configData.config;

        if (payslipData.profile) {
          const halfSalary = Math.round((payslipData.profile.baseSalary / 2) * 100) / 100;
          setProfileName(`${payslipData.profile.firstName} ${payslipData.profile.lastName}`.trim());
          setProfilePosition(payslipData.profile.position);

          if (!payslipData.payslip) {
            setBasicPay(halfSalary);
            if (config) {
              const defaults = buildDefaultsFromConfig(halfSalary, config);
              setAllowances(defaults.allowances);
              setDeductions(defaults.deductions);
            }
            if (payslipData.suggestedBonuses && payslipData.suggestedBonuses.length > 0) {
              setIncentives(payslipData.suggestedBonuses.map((b) => ({
                label: b.isProrated
                  ? `${b.label} (prorated ${b.prorationPct?.toFixed(1) ?? ""}%)`
                  : b.label,
                amount: b.amount,
              })));
            }
          }
        }

        if (payslipData.payslip) {
          setExistingStatus(payslipData.payslip.status);
          setBasicPay(payslipData.payslip.basicPay);
          setAllowances(payslipData.payslip.allowances);
          setIncentives(payslipData.payslip.incentives ?? []);
          setDeductions(payslipData.payslip.deductions);
          setLoanDeductions(payslipData.payslip.loanDeductions);
          setPpaAdjustments(payslipData.payslip.ppaAdjustments ?? []);
          setCashAdvanceDeductions(
            (payslipData.payslip.cashAdvanceDeductions ?? []).map((d) => ({
              label: d.label,
              amount: d.amount,
              cashAdvId: d.cashAdvId,
            })),
          );
          setWorkAdjustments(payslipData.payslip.workAdjustments ?? []);
        } else {
          if (payslipData.pendingInstallments.length > 0) {
            setLoanDeductions(
              payslipData.pendingInstallments.map((inst) => ({
                label: `Salary Loan Deduction (Installment #${inst.seq})`,
                amount: inst.amount,
              })),
            );
          }
          if (payslipData.pendingAdjustments?.length > 0) {
            setPpaAdjustments(
              payslipData.pendingAdjustments.map((a) => ({
                adjId: a.adjId,
                label: a.label,
                amount: a.amount,
                type: a.type,
              })),
            );
          }
          if (payslipData.pendingCashAdvances && payslipData.pendingCashAdvances.length > 0) {
            const grossEstimate = Math.round(
              ((payslipData.profile?.baseSalary ?? 0) / 2) * 100,
            ) / 100;
            const entries: CashAdvanceDeductionEntry[] = [];
            let avail = grossEstimate;
            for (const ca of payslipData.pendingCashAdvances) {
              if (avail <= 0) break;
              const amt = Math.min(ca.remainingAmount, avail);
              if (amt <= 0) continue;
              entries.push({ label: "Cash Advance Deduction", amount: amt, cashAdvId: ca.cashAdvId });
              avail = Math.round((avail - amt) * 100) / 100;
            }
            setCashAdvanceDeductions(entries);
          }
        }
      } catch {
        if (!cancelled) setLoadError("Failed to load payslip data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [userId, period]);

  const totalIncentives = incentives.reduce((s, i) => s + i.amount, 0);
  const grossPay = Math.round(
    (basicPay + allowances.reduce((s, a) => s + a.amount, 0) + totalIncentives) * 100,
  ) / 100;
  const totalStatDeductions = deductions.reduce((s, d) => s + d.amount, 0);
  const totalLoanDeductions = loanDeductions.reduce((s, d) => s + d.amount, 0);
  const totalPpaDeductions = ppaAdjustments.filter((a) => a.type === "DEDUCTION").reduce((s, a) => s + a.amount, 0);
  const totalPpaAdditions = ppaAdjustments.filter((a) => a.type === "ADDITION").reduce((s, a) => s + a.amount, 0);
  const totalCashAdvDeductions = cashAdvanceDeductions.reduce((s, d) => s + d.amount, 0);
  const totalWorkAdj = workAdjustments.reduce((s, d) => s + d.amount, 0);
  const netPay = Math.round((grossPay - totalStatDeductions - totalLoanDeductions - totalCashAdvDeductions - totalPpaDeductions + totalPpaAdditions + totalWorkAdj) * 100) / 100;

  const updateAllowanceAmount = (idx: number, value: string) =>
    setAllowances((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, amount: parseFloat(value) || 0 } : a)),
    );

  const updateDeductionAmount = (idx: number, value: string) =>
    setDeductions((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, amount: parseFloat(value) || 0 } : d)),
    );

  const addIncentive = () =>
    setIncentives((prev) => [...prev, { label: "", amount: 0 }]);

  const removeIncentive = (idx: number) =>
    setIncentives((prev) => prev.filter((_, i) => i !== idx));

  const updateIncentiveLabel = (idx: number, value: string) =>
    setIncentives((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, label: value } : item)),
    );

  const updateIncentiveAmount = (idx: number, value: string) =>
    setIncentives((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, amount: parseFloat(value) || 0 } : item)),
    );

  const addWorkAdj = () =>
    setWorkAdjustments((prev) => [...prev, { label: "", amount: 0 }]);

  const removeWorkAdj = (idx: number) =>
    setWorkAdjustments((prev) => prev.filter((_, i) => i !== idx));

  const updateWorkAdjLabel = (idx: number, value: string) =>
    setWorkAdjustments((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, label: value } : item)),
    );

  const updateWorkAdjAmount = (idx: number, value: string) =>
    setWorkAdjustments((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, amount: parseFloat(value) || 0 } : item)),
    );

  const handleSaveDraft = async () => {
    const invalidIncentive = incentives.find((i) => !i.label.trim());
    if (invalidIncentive) {
      toast.error("All incentives must have a label.");
      return;
    }
    const invalidWorkAdj = workAdjustments.find((w) => !w.label.trim());
    if (invalidWorkAdj) {
      toast.error("All OT / Holiday entries must have a label.");
      return;
    }
    try {
      await savePayslip.mutateAsync({
        userId,
        period,
        ...(businessId ? { businessId } : {}),
        basicPay,
        allowances,
        incentives,
        deductions,
        loanDeductions,
        ppaAdjustments,
        cashAdvanceDeductions,
        workAdjustments: workAdjustments.length > 0 ? workAdjustments : undefined,
      });
      toast.success("Payslip draft saved successfully.");
      onSaved();
      setTimeout(onClose, 1200);
    } catch {
      toast.error("Failed to save payslip. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (loadError) {
    return <p className="text-center text-sm text-[#dc2626] py-4">{loadError}</p>;
  }

  if (existingStatus === "FINALIZED") {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-[#64748b]">
          This payslip is already finalized and cannot be edited.
        </p>
        <Button variant="secondary" onClick={onClose} className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-5">
        <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
          <p className="text-sm font-semibold text-[#0f172a]">{profileName || userId}</p>
          <p className="text-xs text-[#64748b]">
            {profilePosition} — {formatPeriod(period)}
          </p>
        </div>

        {/* Basic Pay */}
        <div>
          <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-3">
            Basic Pay
          </h4>
          <div className="flex items-center gap-3">
            <label className="w-48 shrink-0 text-sm text-[#64748b]">Basic Pay</label>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
              <input
                type="number"
                value={basicPay}
                onChange={(e) => setBasicPay(parseFloat(e.target.value) || 0)}
                className="w-full rounded-lg border border-[#e2e8f0] bg-white pl-7 pr-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Allowances */}
        {allowances.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-3">
              Allowances
            </h4>
            <div className="space-y-3">
              {allowances.map((a, i) => (
                <div key={i} className="flex items-center gap-3">
                  <label className="w-48 shrink-0 text-sm text-[#64748b]">{a.label}</label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
                    <input
                      type="number"
                      value={a.amount}
                      onChange={(e) => updateAllowanceAmount(i, e.target.value)}
                      className="w-full rounded-lg border border-[#e2e8f0] bg-white pl-7 pr-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-transparent"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Incentives */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
              Incentives
            </h4>
            <button
              type="button"
              onClick={addIncentive}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#2447d9] hover:text-[#1e3a8a] transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Incentive
            </button>
          </div>
          {incentives.length > 0 ? (
            <div className="space-y-2">
              {incentives.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item.label}
                    onChange={(e) => updateIncentiveLabel(i, e.target.value)}
                    placeholder="e.g. Performance Bonus"
                    className="flex-1 min-w-0 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-transparent"
                  />
                  <div className="relative w-36 shrink-0">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
                    <input
                      type="number"
                      value={item.amount}
                      onChange={(e) => updateIncentiveAmount(i, e.target.value)}
                      className="w-full rounded-lg border border-[#e2e8f0] bg-white pl-7 pr-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-transparent"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeIncentive(i)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#94a3b8] hover:bg-[#fef2f2] hover:text-[#dc2626] transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {totalIncentives > 0 && (
                <div className="flex justify-end text-xs text-[#2447d9] font-medium pt-1 pr-10">
                  Incentives total: {formatCurrency(totalIncentives)}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-[#94a3b8] italic">
              No incentives — click "Add Incentive" to add one.
            </p>
          )}
        </div>

        <div className="flex justify-between rounded-lg bg-[#eff3ff] px-4 py-2 text-sm font-semibold text-[#2447d9]">
          <span>Gross Pay</span>
          <span>{formatCurrency(grossPay)}</span>
        </div>

        {/* Deductions */}
        <div>
          <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-3">
            Deductions
          </h4>
          <div className="space-y-3">
            {deductions.map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <label className="w-48 shrink-0 text-sm text-[#64748b]">{d.label}</label>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
                  <input
                    type="number"
                    value={d.amount}
                    onChange={(e) => updateDeductionAmount(i, e.target.value)}
                    className="w-full rounded-lg border border-[#e2e8f0] bg-white pl-7 pr-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-transparent"
                  />
                </div>
              </div>
            ))}

            {loanDeductions.map((d) => (
              <div key={d.label} className="flex items-center gap-3">
                <span className="w-48 shrink-0 text-sm text-[#64748b]">{d.label}</span>
                <div className="flex-1 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-3 py-2 text-sm text-[#64748b]">
                  {formatCurrency(d.amount)}{" "}
                  <span className="text-xs text-[#94a3b8]">(auto-deducted)</span>
                </div>
              </div>
            ))}

            {cashAdvanceDeductions.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Cash Advance Deductions</h4>
                {cashAdvanceDeductions.map((d, i) => (
                  <div key={i} className="flex justify-between items-center py-1.5 border-b border-[#f1f5f9]">
                    <span className="text-sm text-[#374151]">{d.label}</span>
                    <span className="text-sm font-medium text-[#dc2626]">- {formatCurrency(d.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            {ppaAdjustments.map((a, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-48 shrink-0 text-sm text-[#64748b] truncate">{a.label}</span>
                <div className={`flex-1 rounded-lg border px-3 py-2 text-sm flex items-center justify-between ${
                  a.type === "DEDUCTION"
                    ? "bg-red-50 border-red-200 text-red-700"
                    : "bg-green-50 border-green-200 text-green-700"
                }`}>
                  <span>
                    {a.type === "DEDUCTION" ? "−" : "+"}{formatCurrency(a.amount)}
                    <span className="text-xs ml-1.5 opacity-70">(PPA {a.type.toLowerCase()})</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setPpaAdjustments((prev) => prev.filter((_, j) => j !== i))}
                    className="ml-2 text-[#94a3b8] hover:text-[#dc2626] transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}

            {deductions.length === 0 && loanDeductions.length === 0 && cashAdvanceDeductions.length === 0 && ppaAdjustments.length === 0 && (
              <p className="text-xs text-[#94a3b8] italic">No deductions configured.</p>
            )}

            <div className="flex justify-between rounded-lg bg-[#fef2f2] px-4 py-2 text-sm font-semibold text-[#dc2626]">
              <span>Total Deductions</span>
              <span>- {formatCurrency(totalStatDeductions + totalLoanDeductions + totalCashAdvDeductions + totalPpaDeductions)}</span>
            </div>
            {totalPpaAdditions > 0 && (
              <div className="flex justify-between rounded-lg bg-green-50 px-4 py-2 text-sm font-semibold text-green-700">
                <span>PPA Additions</span>
                <span>+ {formatCurrency(totalPpaAdditions)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Work Time Adjustments — editable (pre-filled from approved claims, admin can correct) */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-[#d97706] uppercase tracking-wide">
              OT / Holiday Pay
            </h4>
            <button
              type="button"
              onClick={addWorkAdj}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#d97706] hover:text-[#b45309] transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Entry
            </button>
          </div>
          {workAdjustments.length > 0 ? (
            <div className="space-y-2">
              {workAdjustments.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item.label}
                    onChange={(e) => updateWorkAdjLabel(i, e.target.value)}
                    placeholder="e.g. Overtime Pay (2h on 2026-09-15)"
                    className="flex-1 min-w-0 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#d97706]/40 focus:border-[#d97706]"
                  />
                  <div className="relative w-36 shrink-0">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
                    <input
                      type="number"
                      value={item.amount}
                      onChange={(e) => updateWorkAdjAmount(i, e.target.value)}
                      className="w-full rounded-lg border border-[#e2e8f0] bg-white pl-7 pr-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#d97706]/40 focus:border-[#d97706]"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeWorkAdj(i)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#94a3b8] hover:bg-[#fef2f2] hover:text-[#dc2626] transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {totalWorkAdj > 0 && (
                <div className="flex justify-end text-xs text-[#d97706] font-medium pt-1 pr-10">
                  OT / Holiday total: {formatCurrency(totalWorkAdj)}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-[#94a3b8] italic">
              No OT / holiday entries — click "Add Entry" to add one.
            </p>
          )}
        </div>

        <div className="flex justify-between rounded-xl bg-[#eff3ff] border border-[#2447d9]/20 px-5 py-4">
          <span className="text-base font-bold text-[#2447d9]">Net Pay</span>
          <span className="text-2xl font-extrabold text-[#2447d9]">{formatCurrency(netPay)}</span>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={handleSaveDraft}
            fullWidth
            disabled={savePayslip.isPending}
          >
            {savePayslip.isPending ? "Saving…" : "Save Draft"}
          </Button>
        </div>
      </div>
    </>
  );
}
